<template>
	<div class="legend">
		{{ t('dashboard_geo.brand.legend') }}
	</div>
	<div class="chart-wrapper" ref="chartWrapper" v-if="hasData">
		<apexchart ref="chart" type="bubble" height="500" :options="chartOptions" :series="series"
			@mounted="onChartMounted" @updated="onChartUpdated" />
		<!-- Logo overlays (client last so it stays on top) -->
		<div v-for="(competitor, index) in competitorsDataSortedForChart" :key="competitor.name" class="logo-overlay"
			:style="getLogoStyle(competitor, index)">
			<img :src="competitor.logo" :alt="competitor.name" class="competitor-logo" />
		</div>
	</div>

	<div v-else class="no-data">
		<p>{{ geoStore.loading ? t('loading') : t('no_data') }}</p>
	</div>
</template>

<script setup>
import { ref, computed, nextTick, onMounted, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import { useDashboardGeoStore } from '@/store/dashboard/dashboardGeo.js'
import { useMarketStore } from '@/store/global/market'
import { useSubNavigationStore } from '@/store/global/subNavigation'

const { t } = useI18n()
const geoStore = useDashboardGeoStore()
const marketStore = useMarketStore()
const { selectedMarket } = storeToRefs(marketStore)
const { actors, scores } = storeToRefs(geoStore)

const subNavigationStore = useSubNavigationStore()
const { selectedAiTypeDashboard } = storeToRefs(subNavigationStore)

const chart = ref(null)
const chartWrapper = ref(null)
const logoPositions = ref({})
const hoveredLegendSeriesName = ref(null)

const hasData = computed(() => {
	return actors.value.length > 0 && scores.value && scores.value[selectedAiTypeDashboard.value]
})

const competitorsData = computed(() => {
	const aiType = selectedAiTypeDashboard.value

	if (!actors.value.length || !scores.value[aiType]) {
		return []
	}

	const shareOfVoice = scores.value[aiType]?.share_of_voice_by_actor || {}
	const mentions = scores.value[aiType]?.n_mentions_by_actor || {}
	const sources = scores.value[aiType]?.n_source_by_actor || {}

	const result = actors.value.map(actor => {
		const actorKey = String(actor.id)
		
		return {
			name: actor.name,
			actorId: actor.id,
			x: (shareOfVoice[actorKey] || 0) * 100,
			y: mentions[actorKey] || 0,
			z: sources[actorKey] || 0,
			logo: generateLogoUrl(actor.name),
			color: actor.color || '#3498db'
		}
	})

	return result
});

const competitorsDataSortedForChart = computed(() => {
	const list = competitorsData.value
	const nonClient = list.filter(c => c.actorId !== 0)
	const client = list.filter(c => c.actorId === 0)
	return [...nonClient, ...client]
})

const series = computed(() => {
	if (!competitorsDataSortedForChart.value.length) return []

	return competitorsDataSortedForChart.value.map(competitor => ({
		name: competitor.name,
		data: [
			{
				x: competitor.x,
				y: competitor.y,
				z: calculateBubbleSize(competitor.z),
				name: competitor.name,
				logo: competitor.logo,
				fillColor: competitor.color,
				originalZ: competitor.z,
				actorId: competitor.actorId
			}
		]
	}))
});

const extractDomain = (url) => {
	const matches = url.match(/^(?:https?:\/\/)?(?:www\.)?([^\/]+)/i);
	return matches ? matches[1] : url;
}

const generateLogoUrl = (actorName) => {
	const actor = actors.value.find(a => a.name === actorName);
	if (actor && actor.url) {
		const domain = extractDomain(actor.url);
		return `https://img.logo.dev/${domain}?token=pk_PX8Dz-erT0GNa0BxkeJhUg`;
	}
	const encodedName = encodeURIComponent(actorName);
	return `https://ui-avatars.com/api/?name=${encodedName}&size=128&background=random`;
}

const calculateBubbleSize = (sources) => {
	if (!sources || sources === 0) return 0;

	const minSources = 1
	const maxSources = Math.max(500, Math.max(...competitorsData.value.map(c => c.z || 0)))
	const minRadius = 20
	const maxRadius = 60

	const clampedSources = Math.max(minSources, Math.min(maxSources, sources))
	const normalizedValue = (clampedSources - minSources) / (maxSources - minSources)
	return minRadius + (normalizedValue * (maxRadius - minRadius))
}

const calculateLogoPositions = () => {
	if (!chart.value || !chart.value.chart || !chartWrapper.value) return

	nextTick(() => {
		try {
			const chartInstance = chart.value.chart
			const chartEl = chartInstance.el
			const globals = chartInstance.w?.globals || {}
			const collapsedIndices = globals.collapsedSeriesIndices || []
			const seriesNames = globals.seriesNames || []

			const seriesElements = chartEl.querySelectorAll('.apexcharts-series')
			const chartWrapperRect = chartWrapper.value.getBoundingClientRect()

			// Mark all competitors first (hidden ones get visible: false)
			competitorsData.value.forEach((competitor) => {
				const index = seriesNames.indexOf(competitor.name)
				if (index === -1 || collapsedIndices.includes(index)) {
					logoPositions.value[competitor.name] = {
						left: 0,
						top: 0,
						size: 0,
						visible: false
					}
					return
				}

				const seriesEl = seriesElements[index]
				if (!seriesEl) {
					logoPositions.value[competitor.name] = { left: 0, top: 0, size: 0, visible: false }
					return
				}

				const bubble = seriesEl.querySelector('circle')
				if (!bubble) {
					logoPositions.value[competitor.name] = { left: 0, top: 0, size: 0, visible: false }
					return
				}

				const bubbleRect = bubble.getBoundingClientRect()
				const bubbleRadius = parseFloat(bubble.getAttribute('r')) || 20

				const centerX = bubbleRect.left + bubbleRect.width / 2 - chartWrapperRect.left
				const centerY = bubbleRect.top + bubbleRect.height / 2 - chartWrapperRect.top

				// Make logo bigger and ensure it is strictly round (width = height), with diameter limit and minimum
				const logoDiameter = Math.max(60, Math.min(180, bubbleRadius * 3))
				const logoSize = logoDiameter // strictly round, used for both width and height

				logoPositions.value[competitor.name] = {
					left: centerX,
					top: centerY,
					size: logoSize,
					visible: true
				}
			})
		} catch (error) {
			console.warn('Could not calculate logo positions:', error)
		}
	})
}

const bindLegendHoverHandlers = () => {
	if (!chart.value || !chart.value.chart) return

	nextTick(() => {
		try {
			const chartInstance = chart.value.chart
			const chartEl = chartInstance.el

			const legendContainer = chartEl.querySelector('.apexcharts-legend')
			if (legendContainer && legendContainer.dataset.logoLegendBound !== '1') {
				legendContainer.dataset.logoLegendBound = '1'
				legendContainer.addEventListener('mouseleave', () => {
					hoveredLegendSeriesName.value = null
				})
			}

			const legendItems = chartEl.querySelectorAll('.apexcharts-legend-series')
			legendItems.forEach((el) => {
				if (el.dataset.logoLegendBound === '1') return
				el.dataset.logoLegendBound = '1'

				const seriesName =
					(el.getAttribute('seriesname') || '').trim() ||
					(el.querySelector('.apexcharts-legend-text')?.textContent || '').trim()

				if (!seriesName) return

				el.addEventListener('mouseenter', () => {
					hoveredLegendSeriesName.value = seriesName
				})
				el.addEventListener('mouseleave', () => {
					if (hoveredLegendSeriesName.value === seriesName) {
						hoveredLegendSeriesName.value = null
					}
				})
			})
		} catch (error) {
			console.warn('Could not bind legend hover handlers:', error)
		}
	})
}

const getLogoStyle = (competitor, index) => {
	const position = logoPositions.value[competitor.name]
	if (!position) return { display: 'none' }

	const isClient = competitor.actorId === 0
	const hovered = hoveredLegendSeriesName.value
	const opacity = hovered && hovered !== competitor.name ? 0.25 : 1
	return {
		left: `${position.left}px`,
		top: `${position.top}px`,
		display: position.visible ? 'block' : 'none',
		transform: 'translate(-50%, -50%)',
		zIndex: isClient ? 20 : 10,
		opacity,
		width: `${position.size}px`,
		height: `${position.size}px`
	}
}

const onChartMounted = () => {
	setTimeout(() => {
		calculateLogoPositions()
		bindLegendHoverHandlers()
	}, 500)
}

const onChartUpdated = () => {
	setTimeout(() => {
		calculateLogoPositions()
		bindLegendHoverHandlers()
	}, 200)
}

const chartOptions = computed(() => {
	if (!competitorsData.value.length) {
		return {}
	}

	const maxMentions = Math.max(...competitorsData.value.map(c => c.y), 1)
	const maxShare = Math.max(...competitorsData.value.map(c => c.x), 1)
	
	const nonZeroShares = competitorsData.value.map(c => c.x).filter(x => x > 0)
	const hasMultipleNonZero = nonZeroShares.length > 1
	
	let xAxisMax
	if (maxShare >= 80) {
		xAxisMax = 100
	} else if (hasMultipleNonZero) {
		xAxisMax = Math.min(100, maxShare * 1.2)
	} else {
		xAxisMax = Math.min(100, Math.max(maxShare * 1.5, maxShare + 10))
	}
	
	const nonZeroMentions = competitorsData.value.map(c => c.y).filter(y => y > 0)
	const yAxisMax = nonZeroMentions.length > 1 ? maxMentions * 1.2 : Math.max(maxMentions * 1.5, maxMentions + 5)

	return {
		chart: {
			type: 'bubble',
			height: 500,
			zoom: { enabled: false },
			toolbar: {
				show: true,
				tools: {
					download: true,
					selection: false,
					zoom: true,
					zoomin: true,
					zoomout: true,
					pan: true,
					reset: true
				}
			},
			animations: {
				enabled: false,
				easing: 'easeinout',
				speed: 800
			},
			events: {
				zoomed: function () { setTimeout(calculateLogoPositions, 200) },
				scrolled: function () { setTimeout(calculateLogoPositions, 200) },
				dataPointSelection: function () { setTimeout(calculateLogoPositions, 100) },
				legendClick: function () {
					setTimeout(() => {
						calculateLogoPositions()
						bindLegendHoverHandlers()
					}, 150)
				}
			}
		},
		colors: competitorsDataSortedForChart.value.map(c => c.color),
		dataLabels: { enabled: false },
		grid: { borderColor: 'var(--gris-50)' },
		legend: {
			show: true,
			position: 'bottom',
			horizontalAlign: 'center',
			labels: {
				colors: 'var(--gris-600)',
				useSeriesColors: true
			},
			markers: {
				width: 12,
				height: 12,
				radius: 3,
				strokeWidth: 0
			},
			itemMargin: {
				horizontal: 12,
				vertical: 8
			},
			onItemClick: {
				toggleDataSeries: true
			}
		},
		plotOptions: {
			bubble: {
				minBubbleRadius: 20,
				maxBubbleRadius: 60,
				zScaling: true
			}
		},
		tooltip: {
			enabled: true,
			custom: function ({ series, seriesIndex, dataPointIndex, w }) {
				const data = w.globals.initialSeries[seriesIndex].data[dataPointIndex]
				return `
                    <div class="custom-tooltip" style="background-color: var(--blanc); display: flex; flex-direction: column; gap: .4rem">
                        <div style="border-bottom: 1px solid var(--gris-100); padding: .5rem; background-color: var(--gris-50)">
                            ${data.name.charAt(0).toUpperCase() + data.name.slice(1)}
                        </div>
                        <div style="display: flex; flex-direction: column; gap: .4rem; padding: .2rem .4rem .2rem .4rem;">
                            <div>Share: ${Math.round(data.x)}%</div>
                            <div>Mentions: ${data.y.toLocaleString()}</div>
                            <div>Sources: ${data.originalZ || data.z}</div>
                        </div>
                    </div>
                `
			}
		},
		xaxis: {
			type: 'numeric',
			title: {
				text: t('dashboard_geo.landscape.share_of_voice') + ' (%)',
				style: { fontSize: '14px', fontWeight: 600, color: 'var(--gris-600)' }
			},
			min: 0,
			max: xAxisMax,
			tickAmount: xAxisMax >= 50 ? 10 : Math.ceil(xAxisMax / 5),
			decimalsInFloat: 0,
			labels: {
				formatter: function (val) {
					const numVal = Number(val)
					if (isNaN(numVal)) return '0%'
					return Math.round(numVal) + '%'
				},
				style: { colors: 'var(--gris-600)', fontSize: '12px' }
			},
			axisBorder: { show: true, color: 'var(--gris-200)' },
			axisTicks: { show: true, color: 'var(--gris-200)' }
		},
		yaxis: {
			type: 'numeric',
			title: {
				text: t('dashboard_geo.overview_geo.number_mentions'),
				style: { fontSize: '14px', fontWeight: 600, color: 'var(--gris-600)' }
			},
			min: 0,
			max: yAxisMax,
			decimalsInFloat: 0,
			labels: {
				formatter: function (val) {
					const numVal = Number(val)
					if (isNaN(numVal)) return '0'
					return Math.round(numVal).toLocaleString()
				},
				style: { colors: 'var(--gris-600)', fontSize: '12px' }
			},
			axisBorder: { show: true, color: 'var(--gris-200)' }
		},
		markers: {
			hover: { size: undefined, sizeOffset: 3 }
		}
	}
});

const getSelectedDate = () => {
	const selectedDates = JSON.parse(localStorage.getItem('dashboardSelectedDates') || 'null')
	if (selectedDates) {
		const dateToUse = selectedDates.to

		if (dateToUse && dateToUse.includes('-')) {
			const parts = dateToUse.split('-')
			if (parts.length === 3 && parts[0].length === 2) {
				const convertedDate = `${parts[2]}-${parts[1]}-${parts[0]}`
				return convertedDate
			}
		}

		return dateToUse
	}
	return null
}

const refreshData = async () => {
	if (selectedMarket.value) {
		try {
			const selectedDate = getSelectedDate()
			await geoStore.fetchAllGeoData(selectedMarket.value, selectedDate)
		} catch (error) {
			console.error('BrandPerformance: Error refreshing data:', error)
		}
	}
}

watch(() => competitorsData.value, () => {
	nextTick(() => {
		setTimeout(calculateLogoPositions, 300)
	})
}, { deep: true })

watch([actors, scores], () => {
	nextTick(() => {
		setTimeout(calculateLogoPositions, 300)
	})
}, { deep: true })

watch(selectedAiTypeDashboard, (newType, oldType) => {
	nextTick(() => {
		if (chart.value && chart.value.chart) {
			chart.value.chart.updateOptions(chartOptions.value, false, true)
		}
		setTimeout(calculateLogoPositions, 300)
	})
})

watch(competitorsData, (newData, oldData) => {
	nextTick(() => {
		if (chart.value && chart.value.chart && newData.length > 0) {
			chart.value.chart.updateOptions(chartOptions.value, false, true)
			setTimeout(calculateLogoPositions, 300)
		}
	})
}, { deep: true })

onMounted(async () => {
	// await refreshData()

	window.addEventListener('resize', () => {
		setTimeout(calculateLogoPositions, 100)
	})
})
</script>

<style lang="scss" scoped>
.legend {
	text-align: end;
	margin-bottom: 1rem;
}

.chart-wrapper {
	position: relative;
	padding: 1rem .5rem;
}

.logo-overlay {
	position: absolute;
	pointer-events: none;
	transition: all 0.3s ease;
}

.competitor-logo {
	width: 100%;
	height: 100%;
	border-radius: 50%;
	border: 2px solid var(--gris-50);
	background: rgba(255, 255, 255, 0.95);
	object-fit: cover;
}

.custom-tooltip {
	border-radius: 8px;
	font-size: 13px;
}

.no-data {
	color: var(--gris-600);
	font-style: italic;
	text-align: center;
	padding: 2rem;
	font-size: 1.2rem;
}
</style>